/**
 * Subscription Service
 *
 * Handles fetching subscription plans dynamically from Supabase database.
 * Plans are cached for performance and automatically updated when database changes.
 */

import { createClient } from '@supabase/supabase-js';
import { supabase } from '@/api';
import type { Database } from '@/types/supabase';

/** Anon Supabase client used as fallback when the authenticated client's JWT is corrupted. */
const supabaseAnon = createClient<Database>(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  { auth: { persistSession: false, autoRefreshToken: false } }
);

const isCorruptedJwtError = (error: { code?: string; message?: string } | null): boolean => {
  if (!error) return false;
  const msg = (error.message || '').toLowerCase();
  return error.code === 'PGRST301' || msg.includes('not valid base64url') || msg.includes('jwserror');
};

/** Multilingual text stored as JSONB in the DB: {"en": "...", "de": "..."}. */
export type LocalizedText = Record<string, string> | null;

export interface SubscriptionPlan {
  id: string;
  name: string;
  price_monthly: number;
  price_yearly: number | null;
  plan_type: 'monthly' | 'hourly';
  total_compute_hours: number | null;
  max_file_size_mb: number;
  max_uploads_per_month: number;
  max_processing_jobs_per_month: number;
  max_storage_gb: number;
  max_concurrent_jobs: number;
  data_retention_days: number;
  can_use_training: boolean;
  can_export_data: boolean;
  priority_support: boolean;
  max_training_runs_per_month: number;
  created_at: string;
  updated_at: string;

  // Added 2026-04-22 for dynamic pricing (see
  // supabase/migrations/2026-04-22_new_pricing_structure.sql).
  slug: string | null;
  display_name: LocalizedText;
  description: LocalizedText;
  badge_label: LocalizedText;
  cta_label: LocalizedText;
  badge_type: 'popular' | 'recommended' | null;
  cta_type: 'checkout' | 'contact' | 'upgrade_only' | 'register';
  display_order: number | null;
  is_visible_on_landing: boolean;
  is_visible_on_pricing: boolean;
  is_upgrade_only: boolean;
  is_deprecated: boolean;
  has_api_access: boolean;
  max_parallel_apis: number;
  can_use_data_preparation: boolean;
  tier_slug: string | null;
  backend_service_url: string | null;
}

/**
 * Fetch all subscription plans from database
 * Plans are ordered by price (ascending)
 *
 * @returns Array of subscription plans
 */
export async function fetchSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  try {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .order('price_monthly', { ascending: true });

    if (isCorruptedJwtError(error)) {
      // JWT is corrupted — retry with the anon client (plans are public, anon role has read access).
      const fallback = await supabaseAnon
        .from('subscription_plans')
        .select('*')
        .order('price_monthly', { ascending: true });
      if (fallback.error) {
        throw new Error(`Failed to fetch subscription plans: ${fallback.error.message}`);
      }
      // plan_type is 'monthly' | 'hourly' by app convention; not enforced by a DB CHECK constraint.
      return (fallback.data || []) as SubscriptionPlan[];
    }

    if (error) {
      console.error('Error fetching subscription plans:', error);
      throw new Error(`Failed to fetch subscription plans: ${error.message}`);
    }

    // plan_type is 'monthly' | 'hourly' by app convention; not enforced by a DB CHECK constraint.
    return (data || []) as SubscriptionPlan[];
  } catch (error) {
    console.error('Failed to fetch subscription plans:', error);
    throw error;
  }
}

/**
 * Get a specific plan by name
 *
 * @param planName - Name of the plan (Free, Basic, Pro, Enterprise)
 * @returns The subscription plan or null if not found
 */
export async function getSubscriptionPlanByName(
  planName: string
): Promise<SubscriptionPlan | null> {
  try {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('name', planName)
      .single();

    if (error) {
      console.error(`Error fetching plan "${planName}":`, error);
      return null;
    }

    // plan_type is 'monthly' | 'hourly' by app convention; not enforced by a DB CHECK constraint.
    return data as SubscriptionPlan;
  } catch (error) {
    console.error(`Failed to fetch plan "${planName}":`, error);
    return null;
  }
}

/**
 * Format price for display
 *
 * @param amount - Price amount
 * @param currency - Currency code (default: EUR)
 * @returns Formatted price string (e.g., "€29.99")
 */
export function formatPrice(amount: number, currency: string = 'EUR'): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Calculate annual savings percentage
 *
 * @param monthlyPrice - Monthly price
 * @param yearlyPrice - Yearly price
 * @returns Savings percentage (e.g., 17 for 17% savings)
 */
export function calculateAnnualSavings(
  monthlyPrice: number,
  yearlyPrice: number
): number {
  if (monthlyPrice === 0 || yearlyPrice === 0) return 0;

  const monthlyAnnual = monthlyPrice * 12;
  const savings = ((monthlyAnnual - yearlyPrice) / monthlyAnnual) * 100;

  return Math.round(savings);
}

/**
 * Format storage size for display
 *
 * @param sizeGB - Size in gigabytes
 * @returns Formatted storage string (e.g., "20 GB", "500 MB")
 */
export function formatStorage(sizeGB: number): string {
  if (sizeGB >= 1) {
    return `${sizeGB} GB`;
  }
  return `${sizeGB * 1024} MB`;
}

/**
 * Format training runs for display, localized.
 *
 * @param runs - Number of training runs (-1 = unlimited, 0 = none)
 * @param t - i18next translate fn from the caller's `useTranslation('pricing')`
 * @returns Localized string (e.g., "10 runs" / "10 Durchläufe", "Unlimited", "Not available")
 */
export function formatTrainingRuns(
  runs: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (runs === -1) return t('quota.trainingRunsUnlimited');
  if (runs === 0) return t('quota.trainingRunsNotAvailable');
  return t('quota.trainingRunsLabel', { count: runs });
}
